import React from "react";
import { Box, Button, Icon, Table, Text, Badge, InputCheckbox } from "~/components-shared";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import { AuthoritiesEnum, RoutesEnum } from "~/types/enums";
import lists from "~/lib/lists";
import Markdown from "~/components/Markdown";
import { useTranslation } from "react-i18next";
import useAbacExt from "~/hooks/abac";
import progressFlowActionCreators from "~/store/progressFlow/actions";
import { ProgressFlowActionEnum } from "~/store/progressFlow/types";
import { useDispatch } from "react-redux";
const ImpactIcon = styled.span`
  transform: rotate(45deg);
  width: 0.7rem;
  height: 0.7rem;
  background-color: ${_ref => {
  let {
    color
  } = _ref;
  return color;
}};
  display: inline-block;
  margin-right: 0.5rem;
`;
const StyledButton = styled(Button)`
  & span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const ProgressFlowTableRow = _ref2 => {
  let {
    item,
    selection,
    setSelection
  } = _ref2;
  const {
    t
  } = useTranslation();
  const {
    userHas
  } = useAbacExt();
  const theme = useTheme();
  const isAllSelected = selection.includes("*");
  const isIncluded = selection.includes(item.id);
  const isSelected = !isAllSelected && isIncluded || isAllSelected && !isIncluded;
  const dispatch = useDispatch();
  const handleTopicClick = topicId => {
    const url = `${window.location.origin}${RoutesEnum.TOPIC.replace(":topicId", topicId)}?view=footprints`;
    window.open(url, "_blank");
  };
  const handleViewDetails = () => {
    dispatch(progressFlowActionCreators.actionChanged(ProgressFlowActionEnum.DETAILS, null, item));
  };
  return /*#__PURE__*/React.createElement(Table.Tr, {
    key: item.id
  }, userHas(AuthoritiesEnum.INSIGHTS_WRITE) && /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: isSelected,
    onChange: () => setSelection([item.id]),
    className: "progress-flow-item-checkbox",
    s: {
      ml: 4,
      mt: 1
    },
    color: "primary"
  })), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 1
  }, item.topics.length ? item.topics.map(topic => /*#__PURE__*/React.createElement(StyledButton, {
    key: topic.id,
    onClick: () => handleTopicClick(topic.id),
    fontSize: 3,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": topic.name,
    s: {
      px: 2,
      py: 1
    },
    maxWidth: "200px",
    color: "translucentPrimary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "timelapse",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), topic.name)) : /*#__PURE__*/React.createElement(Text, {
    color: "text",
    weight: "semiBold"
  }, "No Linked Topics"), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    fullWidth: true
  }, item.itemType && /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      px: 3,
      py: 0.5
    }
  }, lists.insights.type.names[item.itemType]), item.isDismissed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      px: 3,
      py: 0.5
    }
  }, t("ProgressFlowTableRow.dismissed")), item.isClosed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentSuccess",
    s: {
      px: 3,
      py: 0.5
    }
  }, t("ProgressFlowTableRow.completed")), item.isPrivate && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentWarning",
    s: {
      px: 3,
      py: 0.5
    }
  }, t("ProgressFlowTableRow.private"))))), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: item.context,
    linkColor: "primary"
  })), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Markdown, {
    text: item.summary,
    linkColor: "primary"
  })), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, item.owner.map(owner => /*#__PURE__*/React.createElement(Text, {
    key: owner,
    color: "textSecondary"
  }, owner)))), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: lists.insights.urgency.colors(theme)[item.impact],
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(ImpactIcon, {
    color: lists.insights.urgency.colors(theme)[item.impact]
  }), lists.insights.urgency.names[item.impact])), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, /*#__PURE__*/React.createElement(Box, {
    align: "center"
  }, moment(item.observedAt).format("ddd, MMM Do"))), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, !item.isClosed ? /*#__PURE__*/React.createElement(Text, null, t("ProgressFlowTableRow.open")) : /*#__PURE__*/React.createElement(Text, null, `${t("ProgressFlowTableRow.closed")}, `, /*#__PURE__*/React.createElement(Text, {
    display: "block"
  }, moment(item.closedAt).format("ddd, MMM Do")))), /*#__PURE__*/React.createElement(Table.Td, {
    noHover: true
  }, userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Button, {
    color: "translucent",
    s: {
      px: 1.5,
      py: 1.5,
      mr: 2
    },
    "data-tooltip-id": "progress-flow-item-details-tooltip",
    "data-tooltip-content": t("ProgressFlowTableRow.viewDetails"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      color: theme.colors.text.primary,
      display: "block"
    }),
    onClick: e => {
      e.stopPropagation();
      handleViewDetails();
    },
    rounded: true
  })));
};
export default ProgressFlowTableRow;